import { useState, useEffect } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Search, Filter, Leaf, Star, Sparkles, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { getProducts } from "../lib/api";
import type { ProductWithStats } from "../lib/types";
import { toast } from "sonner";
import { getProductImage } from "../lib/productImageUtils";
import { useAuth } from "../lib/AuthContext";
import { getUserAccessLevel, canAccessProducts, canViewProductNames } from "../lib/auth-helpers";

interface ProductsIndexProps {
  onNavigate?: (page: string, productId?: string) => void;
}

// Fisher-Yates shuffle algorithm for randomization
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export function ProductsIndex({ onNavigate }: ProductsIndexProps) {
  const { user, profile } = useAuth();
  const [products, setProducts] = useState<ProductWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterBrand, setFilterBrand] = useState("all");
  const [sortBy, setSortBy] = useState("name");

  // Redirect non-registered users to signup
  useEffect(() => {
    if (!user) {
      toast.info("Please sign up or sign in to view products");
      onNavigate?.("signup");
    }
  }, [user, onNavigate]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const data = await getProducts({ active: true });
        
        // Randomise products for verified patients on each page load
        const finalData = profile?.is_verified_patient ? shuffleArray(data) : data;
        setProducts(finalData);
        
        if (profile?.is_verified_patient) {
          console.log('🎲 Products randomised for verified patient');
        }
      } catch (error: any) {
        console.error("Error fetching products:", error);
        toast.error("Failed to load products");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [profile?.is_verified_patient]);

  // Get unique brands for filter
  const brands = [...new Set(products.map(p => p.brand).filter(Boolean))].sort() as string[];

  const getGradeFromStars = (avgStars?: number): string => {
    if (!avgStars) return "N/A";
    if (avgStars >= 4.5) return "A+";
    if (avgStars >= 4.0) return "A";
    if (avgStars >= 3.5) return "B+";
    if (avgStars >= 3.0) return "B";
    if (avgStars >= 2.5) return "C+";
    if (avgStars >= 2.0) return "C";
    return "D";
  };

  const getGradeColor = (grade: string) => {
    if (grade.startsWith("A")) return "text-primary";
    if (grade.startsWith("B")) return "text-blue-600";
    if (grade.startsWith("C")) return "text-yellow-600";
    return "text-muted-foreground";
  };

  const filteredProducts = products
    .filter(product => {
      if (filterType !== "all" && product.form !== filterType) return false;
      if (filterBrand !== "all" && product.brand !== filterBrand) return false;
      if (searchQuery) {
        const search = searchQuery.toLowerCase();
        return (
          product.product_name.toLowerCase().includes(search) ||
          product.brand?.toLowerCase().includes(search) ||
          product.sku.toLowerCase().includes(search)
        );
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === "name") {
        return a.product_name.localeCompare(b.product_name);
      }
      if (sortBy === "rating") {
        const aRating = a.rating_stats?.[0]?.avg_stars || 0;
        const bRating = b.rating_stats?.[0]?.avg_stars || 0;
        return bRating - aRating;
      }
      if (sortBy === "reviews") {
        const aReviews = a.rating_stats?.[0]?.review_count || 0;
        const bReviews = b.rating_stats?.[0]?.review_count || 0;
        return bReviews - aReviews;
      }
      return 0;
    });

  // Don't render content if user is not authenticated
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-mesh">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Redirecting to sign up...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6 mb-3">
            <div>
              <h1 className="text-4xl sm:text-5xl mb-3">Medical Cannabis Products</h1>
              <p className="text-xl text-muted-foreground">
                Browse our complete catalogue of medical cannabis products
              </p>
            </div>
            <Button 
              onClick={() => onNavigate && onNavigate("submit-review")}
              className="gradient-green hover:opacity-90 transition-opacity shadow-lg h-12 px-6 shrink-0"
            >
              <Star className="w-4 h-4 mr-2" />
              Write Review
            </Button>
          </div>
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="mb-8"
        >
          <Card className="p-6 glass-card">
            <div className="space-y-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, brand, or SKU..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 h-12 bg-background/50 border-border/50 focus:bg-background transition-colors"
                  />
                </div>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-full md:w-[200px] h-12">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">Name (A-Z)</SelectItem>
                    <SelectItem value="rating">Highest Rated</SelectItem>
                    <SelectItem value="reviews">Most Reviewed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap gap-3">
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-[180px]">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Form" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Forms</SelectItem>
                    <SelectItem value="flower">Flower</SelectItem>
                    <SelectItem value="oil">Oil</SelectItem>
                    <SelectItem value="capsule">Capsule</SelectItem>
                    <SelectItem value="spray">Spray</SelectItem>
                    <SelectItem value="edible">Edible</SelectItem>
                    <SelectItem value="concentrate">Concentrate</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filterBrand} onValueChange={setFilterBrand}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Brand" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Brands</SelectItem>
                    {brands.map((brand) => (
                      <SelectItem key={brand} value={brand}>
                        {brand}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {(searchQuery || filterType !== "all" || filterBrand !== "all") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSearchQuery("");
                      setFilterType("all");
                      setFilterBrand("all");
                    }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Results Count */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          {loading ? (
            <Skeleton className="h-5 w-64" />
          ) : (
            <p className="text-sm text-muted-foreground">
              Showing {filteredProducts.length} of {products.length} products
            </p>
          )}
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="space-y-4">
            {[...Array(10)].map((_, i) => (
              <Card key={i} className="p-6">
                <div className="flex items-center gap-6">
                  <Skeleton className="h-24 w-24 rounded-lg flex-shrink-0" />
                  <div className="flex-1 space-y-3">
                    <Skeleton className="h-6 w-1/2" />
                    <Skeleton className="h-4 w-1/3" />
                    <div className="flex gap-4">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                  </div>
                  <Skeleton className="h-10 w-24" />
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Products List */}
        {!loading && (
          <div className="space-y-4">
            {filteredProducts.map((product, index) => {
              const reviewCount = product.rating_stats?.[0]?.review_count || 0;
              const avgStars = product.rating_stats?.[0]?.avg_stars || 0;
              const grade = getGradeFromStars(avgStars);
              const hasTopRatedBadge = product.badge?.[0]?.qualifies_for_badge || false;

              return (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card
                    className="p-6 glass-card hover-lift cursor-pointer group"
                    onClick={() => onNavigate && onNavigate("product-detail", product.id)}
                  >
                    <div className="flex flex-col sm:flex-row items-start gap-6">
                      {/* Product Image Placeholder */}
                      <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {getProductImage(product.id) ? (
                          <img 
                            src={getProductImage(product.id)!} 
                            alt={product.product_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-4xl opacity-30">🌿</div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap gap-2 mb-2">
                          <Badge className="capitalize">{product.form}</Badge>
                          {hasTopRatedBadge && (
                            <Badge className="gradient-green text-white">
                              <Sparkles className="w-3 h-3 mr-1" />
                              Top Rated
                            </Badge>
                          )}
                          {avgStars > 0 && (
                            <Badge variant="outline" className={getGradeColor(grade)}>
                              Grade: {grade}
                            </Badge>
                          )}
                        </div>

                        <h3 className="text-xl mb-1 group-hover:text-primary transition-colors">
                          {product.product_name}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          {product.brand || "Various Brands"} • SKU: {product.sku}
                        </p>

                        <div className="flex flex-wrap gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">Pack Size:</span>
                            <span className="font-medium">{product.pack_size || "N/A"}</span>
                          </div>
                          {reviewCount > 0 && (
                            <div className="flex items-center gap-1">
                              <Star className="w-4 h-4 fill-current text-primary" />
                              <span className="font-medium">{avgStars.toFixed(1)}</span>
                              <span className="text-muted-foreground">
                                ({reviewCount} {reviewCount === 1 ? "review" : "reviews"})
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* View Button */}
                      <Button
                        variant="outline"
                        className="group-hover:border-primary group-hover:text-primary transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          onNavigate && onNavigate("product-detail", product.id);
                        }}
                      >
                        View Details
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredProducts.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-20"
          >
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-muted/50 flex items-center justify-center">
              <Search className="w-12 h-12 text-muted-foreground" />
            </div>
            <h3 className="text-2xl mb-2">No products found</h3>
            <p className="text-muted-foreground mb-6">
              Try adjusting your search or filters
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery("");
                setFilterType("all");
                setFilterBrand("all");
              }}
            >
              Clear all filters
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
}